import { isUndefined } from 'lodash';
import { useCallback } from 'react';

import {
   IconFillColor,
   IconWrapper,
   InteractiveTable,
   InteractiveTableCellProps,
   QuestionmarkCircleIcon,
   Tooltip,
   useInteractiveTableContext,
} from '@dreryk/react-components';

import { receptionServiceTranslationKeys } from '../../../../../../../translations';
import { ReceptionServiceFormController } from '../../../../ReceptionServiceForm';
import { receptionServicePriceVisitor } from '../../../price/receptionServicePrice.utils';
import {
   ReceptionServicePractitionerUsingDefaultConfiguration,
   ReceptionServicePractitionerValue,
   WithIndividualPrice,
   WithIndividualPriceRange,
} from '../../receptionServicePractitioner.types';

type ReceptionServicePractitionerUsingDefaultConfigurationCellProps = InteractiveTableCellProps<ReceptionServicePractitionerUsingDefaultConfiguration> &
   Pick<ReceptionServiceFormController, 'control'>;

export const ReceptionServicePractitionerUsingDefaultConfigurationCell = (
   props: ReceptionServicePractitionerUsingDefaultConfigurationCellProps
): JSX.Element => {
   const { setCellValue, triggerCell } = useInteractiveTableContext();

   const synchronizeDefaultValues = useCallback(
      (synchronize: boolean) => {
         if (synchronize) {
            const receptionService = props.control.getValues();

            setCellValue<keyof ReceptionServicePractitionerValue>(
               'estimatedDuration',
               props.index,
               receptionService.estimatedDuration
            );

            receptionServicePriceVisitor(receptionService)({
               withPrice: ({ price, priceRangeChecked }) => {
                  setCellValue<keyof WithIndividualPrice>('priceRangeChecked', props.index, priceRangeChecked);
                  setCellValue<keyof WithIndividualPrice>('individualPrice', props.index, price);
                  triggerCell<keyof WithIndividualPrice>('individualPrice', props.index);
               },
               withPriceRange: ({ priceRange, priceRangeChecked }) => {
                  setCellValue<keyof WithIndividualPriceRange>('priceRangeChecked', props.index, priceRangeChecked);
                  setCellValue<keyof WithIndividualPriceRange>('individualPriceRange', props.index, priceRange);
                  triggerCell<keyof WithIndividualPriceRange>('individualPriceRange', props.index);
               },
            });
         }
      },
      [props.control, props.index, setCellValue, triggerCell]
   );

   return (
      <InteractiveTable.CheckboxCell
         testId={props.testId}
         name={props.name}
         index={props.index}
         defaultValue={isUndefined(props.defaultValue) ? true : props.defaultValue}
         onSelectionChange={synchronizeDefaultValues}
      />
   );
};

export const ReceptionServicePractitionerUsingDefaultConfigurationCellHeader = (): JSX.Element => {
   return (
      <Tooltip
         testId={'usingDefaultConfiguration'}
         content={
            receptionServiceTranslationKeys.shared.form.components.practitionersInteractiveTable.headers
               .usingDefaultConfiguration.tooltip
         }
      >
         <IconWrapper testId={'usingDefaultConfiguration'} fillColor={IconFillColor.PRIMARY}>
            <QuestionmarkCircleIcon />
         </IconWrapper>
      </Tooltip>
   );
};
